import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";

/**
 * FRONTEND_HANDOFF.md §3.3 — admin aksiyonlarının ortak onay modali.
 * Her aksiyon geri alınması zor bir yan etki taşıdığı için uyarı metni zorunlu,
 * not alanı denetim kaydına gidiyor.
 */
export type AdminActionDialogProps = {
  open: boolean;
  title: string;
  /** Yan etkiyi anlatan uyarı — modalde her zaman görünür. */
  warning?: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  confirmColor?: "primary" | "error" | "warning" | "success";
  /** Modal içindeki ek alanlar geçerli değilken onayı kapatmak için. */
  confirmDisabled?: boolean;
  loading?: boolean;
  /** Not alanı denetim kaydına yazılır; PII içermemeli. */
  noteLabel?: string;
  noteRequired?: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void | Promise<void>;
  children?: ReactNode;
};

const NOTE_MAX_LENGTH = 500;

export const AdminActionDialog = ({
  open,
  title,
  warning,
  description,
  confirmLabel = "Onayla",
  confirmColor = "primary",
  confirmDisabled,
  loading,
  noteLabel = "Not (denetim kaydına yazılır)",
  noteRequired,
  onClose,
  onConfirm,
  children,
}: AdminActionDialogProps) => {
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  const noteTooLong = note.length > NOTE_MAX_LENGTH;
  const noteMissing = Boolean(noteRequired) && note.trim().length === 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}

          {warning && <Alert severity="warning">{warning}</Alert>}

          {children}

          <TextField
            fullWidth
            multiline
            minRows={2}
            label={noteLabel}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            error={noteTooLong || noteMissing}
            helperText={
              noteTooLong
                ? `En fazla ${NOTE_MAX_LENGTH} karakter`
                : `${note.length}/${NOTE_MAX_LENGTH} — kişisel veri yazmayın`
            }
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={loading}>
          Vazgeç
        </Button>
        <LoadingButton
          variant="contained"
          color={confirmColor}
          loading={loading}
          disabled={noteTooLong || noteMissing || confirmDisabled}
          onClick={() => onConfirm(note.trim())}
        >
          {confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AdminActionDialog;
